<script lang="ts">
  import Icon from '$components/icon/icon.svelte';
  import { MessageSquareText, Info, CircleCheck, MessageSquareWarning, TriangleAlert, X } from 'lucide-svelte';

  interface Props {
    role?: 'neutral' | 'info' | 'positive' | 'warning' | 'negative';
    title?: string;
    showTitle?: boolean;
    showClose?: boolean;
    showIcon?: boolean;
    onclose?: () => void;
  }

  let {
    role = 'neutral',
    title = 'Title',
    showTitle = false,
    showClose = false,
    showIcon = true,
    onclose,
  }: Props = $props();

  const roleIcons = {
    neutral: MessageSquareText,
    info: Info,
    positive: CircleCheck,
    warning: MessageSquareWarning,
    negative: TriangleAlert,
  } as const;

  let roleIcon = $derived(roleIcons[role]);

  function handleClose() {
    onclose?.();

    $host()?.dispatchEvent(new CustomEvent('close', { bubbles: true, composed: true }));
  }
</script>

<div class="message-block a2u-ui-{role}">
  {#if showIcon}
    <div class="icon-area">
      <Icon icon={roleIcon} size={24} />
    </div>
  {/if}
  <div class="content">
    {#if showTitle}
      <p class="title">{title}</p>
    {/if}
    <div class="message-content">
      <slot></slot>
    </div>
  </div>
  {#if showClose}
    <button class="close-btn" aria-label="Close" onclick={handleClose}>
      <Icon icon={X} size={16} />
    </button>
  {/if}
</div>

<style lang="scss">
  .message-block {
    display: flex;
    width: 100%;
    align-items: flex-start;
    box-sizing: border-box;
    overflow: hidden;
    border-radius: var(--a2u-ui-border-radius-size-base-md);
    border-width: var(--a2u-ui-border-width-base-md);
    border-style: solid;
    padding: var(--a2u-ui-space-around-base-sm);
    gap: var(--a2u-ui-space-between-base-sm);
  }

  .icon-area {
    flex-shrink: 0;
  }

  .content {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: var(--a2u-ui-space-between-base-xs);
    min-width: 0;
    font: var(--a2u-ui-font-body-copy-standard);
  }

  .title {
    font: var(--a2u-ui-font-body-title-strong);
    margin: 0;
  }

  .message {
    font: var(--a2u-ui-font-body-copy-standard);
    margin: 0;
  }

  .close-btn {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
  }

  .a2u-ui-neutral {
    background: var(--a2u-ui-color-bg-emphasis-subtle);
    border-color: var(--a2u-ui-color-border-emphasis-subtle);
    color: var(--a2u-ui-color-text-on-emphasis-subtle);

    .icon-area { --icon-color: var(--a2u-ui-color-icon-on-emphasis-subtle); }
    .close-btn { --icon-color: var(--a2u-ui-color-text-on-emphasis-subtle); }
  }

  .a2u-ui-info {
    background: var(--a2u-ui-color-bg-info-subtle);
    border-color: var(--a2u-ui-color-border-info-subtle);
    color: var(--a2u-ui-color-text-on-info-subtle);

    .icon-area { --icon-color: var(--a2u-ui-color-icon-on-info-subtle); }
    .close-btn { --icon-color: var(--a2u-ui-color-text-on-info-subtle); }
  }

  .a2u-ui-positive {
    background: var(--a2u-ui-color-bg-positive-subtle);
    border-color: var(--a2u-ui-color-border-positive-subtle);
    color: var(--a2u-ui-color-text-on-positive-subtle);

    .icon-area { --icon-color: var(--a2u-ui-color-icon-on-positive-subtle); }
    .close-btn { --icon-color: var(--a2u-ui-color-text-on-positive-subtle); }
  }

  .a2u-ui-warning {
    background: var(--a2u-ui-color-bg-warning-subtle);
    border-color: var(--a2u-ui-color-border-warning-subtle);
    color: var(--a2u-ui-color-text-on-warning-subtle);

    .icon-area { --icon-color: var(--a2u-ui-color-icon-on-warning-subtle); }
    .close-btn { --icon-color: var(--a2u-ui-color-text-on-warning-subtle); }
  }

  .a2u-ui-negative {
    background: var(--a2u-ui-color-bg-negative-subtle);
    border-color: var(--a2u-ui-color-border-negative-subtle);
    color: var(--a2u-ui-color-text-on-negative-subtle);

    .icon-area { --icon-color: var(--a2u-ui-color-icon-on-negative-subtle); }
    .close-btn { --icon-color: var(--a2u-ui-color-text-on-negative-subtle); }
  }
</style>
